import React, { useEffect, useState } from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
  useRowSelect,
  useExpanded,
} from "react-table";
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
} from "@mui/material";
import Empty from "../error/Empty";
import TableFooter from "./sub-components/TableFooter";
import TableHeader from "./sub-components/TableHeader";
import TableFeature from "./sub-components/TableFeature";
import TableCustomizerDialog from "./sub-components/TableCustomizerDialog";
import { exportData, getExportableData } from "../../manager/dataManager";
import TableLoader from "../TableLoader";
import PurcchseOrderActionModal from "../PurcchseOrderActionModal";
import { poDefaultColumn } from "./cell/poTableCustonCell";
import TableFilterPopOver from "./sub-components/TableFilterPopOver";
import TableHeaderLayout from "./sub-components/TableHeaderLayout";
import { poStatusHandler, PO_VIEWED } from "../../manager/actionManager";
import POUpdateModal from "../POUpdateModal";
import POPriceUpdateModal from "../POPriceUpdateModal";
import { getUser } from "../../services/authService";
import TableCheckbox from "./sub-components/TableCheckbox";
import { Download } from "@mui/icons-material";
import { useParams } from "react-router-dom";

function PurchaseOrderTable({
  columns,
  data,
  fromDate,
  toDate,
  handleFromDate,
  handleToDate,
  callAPI,
  PONumber,
  setPONumber,
  loading,
  getOverDuePO,
  activeCard,
  totalOverdue,
  expandedTable,
  excelData,
}) {
  const { POID, module } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showTableCustomizer, setShowTableCustomizer] = useState(false);
  const [purchaseOrderAction, setPurchaseOrderAction] = useState(null);
  const [selectedPOForUpdate, setSelectedPOForUpdate] = useState(null);
  const [selectedPOForPriceUpdate, setSelectedPOForPriceUpdate] =
    useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (POID !== "list" && !module) {
      setPurchaseOrderAction(POID);
    } else if (module === "price-changed") {
      setSelectedPOForUpdate({ POID });
    } else if (module === "po-changed") {
      setSelectedPOForPriceUpdate({ POID });
    }
    return () => {};
  }, [POID, module]);

  const user = getUser();

  const open = Boolean(anchorEl);

  const handleDoubleClick = (rowData) => {
    setPurchaseOrderAction(rowData.POID);
    if (user?.isSupplierOperation || user?.isSupplierFinance) {
      if (rowData.LatestAction === "New") {
        poStatusHandler(rowData, PO_VIEWED);
      }
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    pageOptions,
    state,
    setPageSize,
    canPreviousPage,
    setGlobalFilter,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps,
    selectedFlatRows,
    gotoPage,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: poDefaultColumn,
      initialState: {
        pageSize: 12,
        hiddenColumns: columns
          .filter((col) => col.show === false)
          .map((col) => col.accessor),
      },
      getSubRows: (row) => {
        if (row?.subRows) {
          return row?.subRows;
        }
        return [];
      },
      autoResetHiddenColumns: false,
      setSelectedPOForUpdate,
      setSelectedPOForPriceUpdate,
      handleDoubleClick,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        return [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <TableCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
              <>
                {(row.subRows.length !== 0 || !expandedTable) && (
                  <TableCheckbox {...row.getToggleRowSelectedProps()} />
                )}
              </>
            ),
          },
          ...columns,
        ];
      });
    }
  );

  const { pageIndex, pageSize, globalFilter } = state;

  return (
    <>
      <POUpdateModal
        handleClose={() => {
          setSelectedPOForUpdate(null);
        }}
        POID={selectedPOForUpdate?.POID}
        selectedPO={selectedPOForUpdate}
      />
      <POPriceUpdateModal
        handleClose={() => {
          setSelectedPOForPriceUpdate(null);
        }}
        POID={selectedPOForPriceUpdate?.POID}
        selectedPrice={selectedPOForPriceUpdate}
      />
      <TableCustomizerDialog
        open={showTableCustomizer}
        handleClose={() => setShowTableCustomizer(false)}
        allColumns={allColumns}
        getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
      />
      <PurcchseOrderActionModal
        purchaseOrderID={purchaseOrderAction}
        handleClose={() => {
          setPurchaseOrderAction(null);
        }}
      />

      <TableFilterPopOver
        open={open}
        handleClose={handleClose}
        anchorEl={anchorEl}
        fromDate={fromDate}
        toDate={toDate}
        callAPI={callAPI}
        setPONumber={setPONumber}
        PONumber={PONumber}
        handleFromDate={handleFromDate}
        handleToDate={handleToDate}
      />
      <TableHeaderLayout
        setGlobalFilter={setGlobalFilter}
        globalFilter={globalFilter}
      >
        {selectedFlatRows.length === 0 ? (
          <TableFeature
            handleFilter={handleClick}
            openCustomizer={() => setShowTableCustomizer(true)}
            refresh={callAPI}
            exportHandler={() => {
              exportData(
                getExportableData(excelData, columns),
                `Purchase Order ${fromDate} to ${toDate}`
              );
            }}
            handleOverDue={getOverDuePO}
            activeCard={activeCard}
            totalOverdue={totalOverdue}
            fromDate={fromDate}
            toDate={toDate}
          />
        ) : (
          <Button
            variant="outlined"
            endIcon={<Download sx={{ fontSize: "10px" }} />}
            size="small"
            onClick={() => {
              exportData(
                getExportableData(
                  excelData.filter((item) => {
                    if (
                      selectedFlatRows.filter((row) => {
                        if (item.PONumber === row.original.PONumber) {
                          return row.original.PONumber;
                        }
                      })[0]
                    ) {
                      return item;
                    }
                  }),
                  columns
                ),
                `Purchase Order ${fromDate} to ${toDate}`
              );
            }}
          >
            Export Selected
          </Button>
        )}
      </TableHeaderLayout>
      {data.length === 0 && loading === false && (
        <Empty title={"No data found"} />
      )}
      {data.length !== 0 && loading === false && (
        <>
          <TableContainer component={Paper}>
            <Table
              {...getTableProps()}
              sx={{
                borderCollapse: "separate",
                minWidth: "max-content",
              }}
            >
              <TableHeader headerGroups={headerGroups} />
              <TableBody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow
                      {...row.getRowProps()}
                      sx={{
                        "& td": {
                          bgcolor: "background.paper",
                          ...(expandedTable && {
                            bgcolor:
                              row.subRows.length !== 0
                                ? "background.paper"
                                : "#f3f3f3",
                          }),
                          cursor: "pointer",
                        },
                      }}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TableFooter
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            pageOptions={pageOptions}
            pageIndex={pageIndex}
            nextPage={nextPage}
            previousPage={previousPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            gotoPage={gotoPage}
            pageCount={pageCount}
          />
        </>
      )}
      {loading === true && <TableLoader title={"Loading..."} />}
    </>
  );
}

export default PurchaseOrderTable;
